// src/features/participants/page/Category/PolicyHolder.tsx
import { useState } from 'react';
import type { Key } from 'antd/es/table/interface';
import DynamicTable from '@/common/components/DynamicTable/DynamicTable';
import { Button, Typography, Modal, Form, Input, notification, Space, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import dayjs from 'dayjs';
import Confirmation from '@/common/components/ConfirmButton';
import { SyncOutlined, ContainerOutlined } from '@ant-design/icons';
import CancelPolicyHolderModal from '../../components/CancelPolicyHolderModal';
import { useRouter } from "@tanstack/react-router";
import StatusTag from '@/common/components/Status';
import { POLICY_HOLDER_STATUS_OPTIONS } from '@/common/staticData/options';
import { hasPermission } from '@/common/services/permissionService';
import { getPermissionKeyFromApi } from '@/common/components/fromatPermission';
import { t } from 'i18next';
import { post } from '../../../../common/services/httpClient';
import { useMutation } from '@tanstack/react-query';
import type { PolicyHolderType } from '../../types/participantsTypes';

type PolicyHolderChildType = {
    id: number;
    name: string;
    relation: string;
    dob: string | null;
    status: string;
};

const PolicyHolder = () => {
    const [selectedPolicyRowKeys, setSelectedPolicyRowKeys] = useState<Key[]>([]);
    const [updateEmailVisible, setUpdateEmailVisible] = useState(false);
    const [currentRecord, setCurrentRecord] = useState<PolicyHolderType | null>(null);
    const [form] = Form.useForm();
    const router = useRouter();
    const [tableRefreshKey, setTableRefreshKey] = useState<number>(0); // used to refresh table after update

    const handleRowSelectionChange = (selectedRowKeys: Key[]) => {
        setSelectedPolicyRowKeys(selectedRowKeys);
        //console.log("Selected Policy IDs:", selectedRowKeys);
    };

    // Open Update Email Modal
    const openUpdateEmailModal = (record: PolicyHolderType) => {
        setCurrentRecord(record);
        form.setFieldsValue({
            officeEmail: record.officeEmail,
            phoneNo: record.phoneNo,
            mobileNo: record.mobileNo,
            address: record.address || ''
        });
        setUpdateEmailVisible(true);
    };

    // Submit email/phone/address update
    const editContactMutation = useMutation({
        mutationFn: (data: {
            id: number;
            email: string;
            phoneNo: string;
            mobileNo: string;
            address: string;
        }) => post('/policyholder/updatecontact', data),

        onSuccess: (data: any) => {
            if (data?.isError) {
                notification.error({
                    message: data?.title || t('Error'),
                    description: data?.message,
                });
                return;
            }

            notification.success({
                message: t('Information updated successfully'),
            });

            setUpdateEmailVisible(false);
            setCurrentRecord(null);
            form.resetFields();

            // Refresh table
            setTableRefreshKey(prev => prev + 1);
        },

        onError: (error: any) => {
            notification.error({
                message: t('Error'),
                description: error?.message || t('Something went wrong'),
            });
        },
    });


    const onUpdateEmail = (values: {
        officeEmail: string;
        phoneNo: string;
        mobileNo: string;
        address: string;
    }) => {
        if (!currentRecord) return;



        editContactMutation.mutate({
            id: currentRecord.id,
            officeEmail: values.officeEmail,
            phoneNo: values.phoneNo,
            mobileNo: values.mobileNo,
            address: values.address,
        });
    };


    const myActions = [
        {
            render: (record: PolicyHolderType) => {
                if (record.status === "Created" && record.policyCode && record?.policyStatus?.toLowerCase() === "active") {
                    return (
                        <Confirmation
                            apiUrl={API_ENDPOINTS.PARTICIPANTS.RESYNC_POLICY}
                            id={record.id}
                            title={t("Activate")}
                            method="post"
                            icon={<SyncOutlined />}
                            payloadKey="id"
                            confirmButtonText={t("Are you sure you want to 'Activate' this policy holder?")}
                            queryKey={QUERY_KEYS.PARTICIPANTS.POLICYHOLDERS}
                            successMessage={t("Policy synced successfully")}
                        />
                    );
                }

                if (record.status === "Active") {
                    return (
                        <Space size="middle">
                            <CancelPolicyHolderModal
                                apiUrl={API_ENDPOINTS.PARTICIPANTS.CANCEL_POLICY_HOLDER}
                                record={record}
                                title={t("Cancel")}
                                confirmButtonText={t("Are you sure you want to cancel the policy for selected policy holder?")}
                                queryKey={QUERY_KEYS.PARTICIPANTS.POLICYHOLDERS}
                                successMessage={t("Policy cancelled successfully")}
                            />
                            {
                                hasPermission(getPermissionKeyFromApi(API_ENDPOINTS.PARTICIPANTS.UPDATE_POLICYHOLDER_CONTACT)) &&

                                <Tooltip title={t("Edit Contact")}>
                                    <Button
                                        icon={<ContainerOutlined />}
                                            onClick={() => openUpdateEmailModal(record)}
                                            style={{
                                                borderRadius: '6px',
                                                height: '32px',
                                                width: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: `1px solid`,
                                                transition: 'all 0.2s ease'
                                            }}
                                    />
                                </Tooltip>



                            }
                        </Space>
                    )
                }

                
                return <span></span>;
            },
        },
    ];

    // Table columns
    const policyHolderColumns: ColumnsType<PolicyHolderType> = [
        {
            title: 'Card Number',
            key: 'cardNumber',
            fixed: 'left',
            render: (_, record) => (
                <>
                    <Button
                        type="link"
                        onClick={() => {
                            router.navigate({
                                to: '/participants/policy-holder/Detail',
                                state: { record } as Record<string, unknown>,
                            });
                        }}
                    >
                        {record.cardNumber}
                    </Button>
                    <Typography.Text copyable={{ text: record.cardNumber }}></Typography.Text>
                </>
            ),
        },
        { title: "Name", dataIndex: "name", key: "name" },
        { title: "DOB", dataIndex: "dob", key: "dob", render: (text: string) => (text ? dayjs(text).format('DD-MM-YYYY') : '') },
        { title: "NationalID No", dataIndex: "nationalIDNo", key: "nationalIDNo" },
        { title: "Gender", dataIndex: "gender", key: "gender" },
        { title: "Category", dataIndex: "category", key: "category" },
        { title: "Company Name", dataIndex: "companyName", key: "companyName" },
        { title: "Policy Code", dataIndex: "policyCode", key: "policyCode" },
        { title: "Mobile No", dataIndex: "mobileNo", key: "mobileNo" },
        { title: "Email", dataIndex: "officeEmail", key: "officeEmail" },
        { title: "Phone No", dataIndex: "phoneNo", key: "phoneNo" },
        { title: "Address", dataIndex: "address", key: "address" },
        { title: "Passport No", dataIndex: "passportNo", key: "passportNo" },
        { title: "Country", dataIndex: "country", key: "country" },
        { title: "Active", dataIndex: "isActive", key: "isActive", render: (isActive: boolean) => <StatusTag status={isActive ? "Yes" : "No"} /> },
        { title: 'Status', dataIndex: 'status', key: 'status', fixed: 'right', render: (status: string) => <StatusTag status={status} /> },
    ];

    //Temperory Removed Dependent
    //const expandedRowRender = (record: PolicyHolderType) => (
    //    <div style={{ margin: "0 25px" }}>
    //        <DynamicTable<PolicyHolderChildType>
    //            tableTitle={`${t("Dependent")} - ${record.name}`}
    //            dataApiUrl={API_ENDPOINTS.PARTICIPANTS.SEARCH_POLICYHOLDER_DEPENDENT}
    //            baseQueryKey={["PolicyHolderChildren", String(record.id)]}
    //            columns={childColumns}
    //            showActions={false}
    //            addNewPermission="false"
    //            extraParameters={{ Id: record.id }}
    //        />
    //    </div>
    //);

    {/*expandable={{*/ }
    {/*    expandedRowRender,*/ }
    {/*    rowExpandable: (record) => record.hasDependent === true,*/ }
    {/*}}*/ }

    return (
        <div>
            <DynamicTable<PolicyHolderType>
                key={tableRefreshKey} // refresh table after update
                dataApiUrl={API_ENDPOINTS.PARTICIPANTS.SEARCH_POLICYHOLDER}
                baseQueryKey={QUERY_KEYS.PARTICIPANTS.POLICYHOLDERS}
                columns={policyHolderColumns}
                tableTitle={t("Policy holder(s)")}
                secondButtonTitle="Save Bulk"
                secondButtonUrl="/participants/policy-holder/bulkForm"
                secondButtonPermissionKey={API_ENDPOINTS.PARTICIPANTS.SAVE_BULK_POLICYHOLDER}
                showActions={{ edit: (record) => !record.isActive && record.status === "Created", delete: (record) => !record.isActive && record.status === "Created" }}
                rowSelectionEnabled={true}
                rowSelectable={(record: PolicyHolderType) => record.status === "Active"}
                onRowSelectionChange={handleRowSelectionChange}
                selectedRowKeys={selectedPolicyRowKeys}
                customActions={myActions}
                deleteBaseUrl={API_ENDPOINTS.PARTICIPANTS.DELETE_POLICYHOLDER}
                deletePayloadField={"cardNumber"}
                editBaseUrl={(record) => record.parentPolicyHolderId ? "/participants/policy-holder/DependentForm" : "/participants/policy-holder/form"}
                headerActions={[
                    hasPermission(getPermissionKeyFromApi(API_ENDPOINTS.PARTICIPANTS.CANCEL_POLICY_HOLDER)) &&
                    <CancelPolicyHolderModal
                        key="bulk-cancel"
                        apiUrl={API_ENDPOINTS.PARTICIPANTS.CANCEL_POLICY_HOLDER}
                        title={t("Cancel Selected")}
                        confirmButtonText={t("Are you sure you want to 'Cancel' the selected policy holders?")}
                        queryKey={QUERY_KEYS.PARTICIPANTS.POLICYHOLDERS}
                        successMessage={t("Selected policy holders cancelled successfully")}
                        selectedRowKeys={selectedPolicyRowKeys}
                    />,
                ]}

                filterConfigs={[
                    {
                        field: 'companyId',
                        type: 'select',
                        placeholder: "Select company",
                        showSearch: true,
                        dynamicOptions: {
                            url: "/Lookup/GetDropdownData",
                            method: "POST",
                            body: { dropdowns: ["company"] },
                            transformData: (apiResponse: unknown) => {
                                const response = apiResponse as { company: { Name: string; Id: number }[] };
                                return response.company.map((item) => ({ label: item.Name, value: item.Id }))
                            }
                        },
                    },
                    {
                        field: "searchBy",
                        placeholder: "Select option",
                        type: 'select',
                        showSearch: true,
                        options: [
                            { label: 'Name', value: 'Name' },
                            { label: 'Card Number', value: 'CardNumber' },
                            { label: 'Policy Code', value: 'PolicyCode' }
                        ]
                    },
                    { field: "searchValue", placeholder: "Search by policy holder name", type: 'input' },
                    {
                        field: "status",
                        placeholder: "Select Status",
                        type: 'select',
                        showSearch: true,
                        options: POLICY_HOLDER_STATUS_OPTIONS
                    },
                    {
                        field: "Print",
                        type: "export",
                        exportOptions: {
                            url: API_ENDPOINTS.PARTICIPANTS.EXPORT_POLICYHOLDER,
                            fileName: "PolicyHolder",
                            fileType: "excel",
                            defaultPayload: {},
                            columns: [
                                { header: "Card Number", key: "cardNumber" },
                                { header: "Name", key: "name" },
                                {
                                    header: "DOB",
                                    key: "dob",
                                    transform: (date: string) => date ? dayjs(date).format('DD-MM-YYYY') : "-"
                                },
                                { header: "Category", key: "category" },
                                { header: "Company Name", key: "companyName" },
                                { header: "Policy Code", key: "policyCode" },
                                { header: "Active", key: "isActive", transform: (val: boolean) => val ? "Active" : "Inactive" },
                                { header: "Status", key: "status" }
                            ]
                        }
                    }
                ]}
            />

            {/* Inline Update Modal */}
            <Modal
                title={
                    currentRecord ? (
                        <div>
                            <div style={{ fontWeight: 600 }}>
                                {currentRecord.name} ({currentRecord.cardNumber})
                            </div>
                            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 8 }} />
                        </div>
                    ) : (
                        t("Update Contact Info")
                    )
                }
                open={updateEmailVisible}
                onCancel={() => setUpdateEmailVisible(false)}
                footer={null}
            >
                <Form form={form} layout="vertical" onFinish={onUpdateEmail}>
                    <Form.Item
                        name="officeEmail"
                        label={t("Email")}
                        rules={[
                            {
                                pattern: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                message: t("Please enter a valid email"),
                            },
                        ]}
                    >
                        <Input placeholder={t("Email")} />
                    </Form.Item>


                    <Form.Item
                        name="phoneNo"
                        label={t("Phone No")}
                        rules={[
                            { pattern: /^(?!.*\s)\+?[0-9]{7,20}$/, message: t("Please enter a valid phone number") },
                        ]}
                    >
                        <Input placeholder={t("Phone No")} />
                    </Form.Item>

                    <Form.Item
                        name="mobileNo"
                        label={t("Mobile No")}
                        rules={[
                            { pattern: /^(?!.*\s)\+?[0-9]{7,20}$/, message: t("Please enter a valid mobile number") },
                        ]}
                    >
                        <Input placeholder={t("Mobile No")} />
                    </Form.Item>

                    <Form.Item
                        name="address"
                        label={t("Address")}
                        rules={[
                            { max: 250, message: t('Address cannot be more than 250 characters long.') },
                        ]}
                    >
                        <Input placeholder={t("Address")} />
                    </Form.Item>
                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            block
                            loading={editContactMutation.isPending}
                        >
                            {t("Update")}
                        </Button>

                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default PolicyHolder;